import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { currentBrand } from "@/lib/brand";
import { ArrowLeft, BookOpen } from "lucide-react";
import { useEffect } from "react";
import { staffTopics } from "@/lib/help-content";
import { HelpTopicGrid } from "@/components/help/HelpTopicGrid";

export const Route = createFileRoute("/help/staff")({
  component: HelpStaff,
});

function HelpStaff() {
  const { profile } = useAuth();
  const nav = useNavigate();
  const isStaff = profile?.role === "staff" || profile?.role === "admin";

  useEffect(() => {
    if (profile && !isStaff) {
      nav({ to: "/help" });
    }
  }, [profile, isStaff, nav]);

  if (!isStaff) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background animate-in fade-in slide-in-from-bottom-4 duration-200 flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-background">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <img
              src={currentBrand.logoSrc}
              alt={`${currentBrand.name} logo`}
              className="h-8 w-auto"
            />
            <h1 className="text-lg font-semibold">Staff Help</h1>
          </div>
          <Link
            to="/help"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Help
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-hero text-primary-foreground">
                <BookOpen className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Staff Help</h1>
                <p className="text-muted-foreground">
                  Staff-specific documentation for managing jobs, customers, and system settings.
                </p>
              </div>
            </div>
          </div>

          <HelpTopicGrid topics={staffTopics} />

          <div className="mt-8 rounded-3xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold mb-2">Open the in-app help</h2>
            <p className="text-sm text-muted-foreground mb-4">
              The same content is available inside the app at /app/help with quick links into
              Settings.
            </p>
            <Link
              to="/app/help"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-hero px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-soft hover:opacity-90"
            >
              Open in-app help
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-sky-50 dark:bg-sky-950/20 py-8 border-t border-border">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src={currentBrand.logoSrc}
                alt={`${currentBrand.name} logo`}
                className="h-6 w-auto"
              />
              <span className="text-sm font-medium">{currentBrand.name}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {currentBrand.name}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
